import React, {useEffect} from "react";
import {useParams} from "react-router-dom";
import useGoToNavigation from "../../../../util/useGotoNavigation";
import {useEditMode, useModal} from "../../../../hooks";
import {SubmitHandler, useForm, useWatch} from "react-hook-form";
import {ProjectResType} from "../interface";
import {useCreateProject, useDeleteProject, useUpdateProject} from "../hooks";
import {useUser} from "../../../../util/userContext/UserContext";
import moment from "moment";
import {useReadProjectDetail} from "../hooks/useReadProjectDetail";
import {SearchBusinessPartnerInner} from "../../../../components/common/modal/inner/SearchBusinessPartnerInner";
import {SearchContactsInner} from "../../../../components/common/modal/inner/SearchContactsInner";
import {useQueryClient} from "@tanstack/react-query";
import SearchUserInner from "../../../../components/common/modal/inner/SearchUserInner";
import {SearchErpInner} from "../../../../components/common/modal/inner/SearchErpInner";
import AuthorityChecker from "../../../../components/common/authorityGuard/AuthorityChecker";

type Props = {
    isCreate?: boolean;
};

export const ProjectDetail = ({isCreate = false}: Props) => {
    const {id} = useParams();

    const {handleModal, resetModal} = useModal();
    const {data} = useReadProjectDetail({id: id ? Number(id as string) : 0});
    const {goToNavigation} = useGoToNavigation();
    const {mode, setRead, setUpdate, setCreate} = useEditMode();
    const {user} = useUser()
    const qc = useQueryClient()

    const {mutate: updateMutate} = useUpdateProject(() => {
        setRead()
    });
    const {mutate: createMutate} = useCreateProject(() => {
        setRead()
    });
    const {mutate: deleteMutate} = useDeleteProject({});

    const defaultValue = {
        assignedPersonnel: "",
        contact: "",
        customerAccountId: "",
        description: "",
        endDt: "",
        pm: "",
        pmName: "",
        projectId: 0,
        projectName: "",
        regBy: user?.userId ? String(user?.userId) : "0",
        regName: "",
        remark: "",
        salesAccountId: "",
        salesAccountName: "",
        startDt: "",
        state: 1,
        possibility: 0,
    };
    const {register, handleSubmit, reset, setValue, control} = useForm({
        defaultValues: isCreate ? defaultValue : data,
    });

    const watchesBasicInfo = {
        ...isCreate ? defaultValue : data,
        ...useWatch<ProjectResType>({control}),
    };

    const update = () => {
        setUpdate();
    };

    const onSubmit: SubmitHandler<ProjectResType> = (submitData) => {
        if (submitData.startDt === "") {
            handleModal({isOpen: true, contents: "시작일을 입력해주세요.", type: "alert"})
        } else if (submitData.endDt === "") {
            handleModal({isOpen: true, contents: "종료일을 입력해주세요.", type: "alert"})
        } else {
            if (mode === "C") {
                createMutate({...submitData, regBy: String(user?.userId) ?? "0"});
            } else {
                updateMutate(submitData);
            }
        }
    };

    useEffect(() => {
        if (isCreate) {
            setCreate();
        } else {
            // reset(data);
        }
    }, [isCreate]);

    return (
        <>
            <div className="content_wrap">
                <div className="fiter_top">
                    <h3 className="filter_heading">프로젝트 {isCreate ? "생성" : "상세보기"}</h3>
                    <div className="filter_toolbar">
                        <label className="form-label m-0 w-120px">상태</label>
                        <select
                            className="form-select h-34px w-150px"
                            disabled={mode === "R"}
                            {...register("state")}
                        >
                            <option value="1">요청</option>
                            <option value="2">진행</option>
                            <option value="3">성공</option>
                            <option value="4">실패</option>
                        </select>
                        {mode !== "R" || isCreate ? (
                            <React.Fragment>
                                <button
                                    className="btn btn-primary w-100px h-38px ms-40px"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    저장
                                </button>
                                <button
                                    className="btn btn-outline-secondary w-100px h-38px"
                                    onClick={() => {
                                        if (isCreate) {
                                            goToNavigation("customer/project/list", {}, true);
                                            reset(defaultValue);
                                        } else {
                                            reset(data);
                                        }
                                        setRead();
                                    }}
                                >
                                    취소
                                </button>
                            </React.Fragment>
                        ) : (
                            <>
                                <button
                                    className="btn btn-outline-primary w-100px h-38px ms-40px"
                                    onClick={() => {
                                        goToNavigation("customer/project/list", {}, true);
                                    }}
                                >
                                    <img
                                        src={
                                            process.env.PUBLIC_URL +
                                            "/assets/images/icon_hamburger_btn.png"
                                        }
                                        alt=""
                                    />
                                    목록
                                </button>
                                <AuthorityChecker authorityNames={['projectWriteYn']}>
                                    <button
                                        className="btn btn-secondary w-100px h-38px"
                                        onClick={() => {
                                            update();
                                        }}
                                    >
                                        수정
                                    </button>
                                    <button
                                        className="btn btn-outline-dark w-100px h-38px"
                                        onClick={() => {
                                            handleModal({
                                                isOpen: true,
                                                contents: "삭제하시겠습니까?",
                                                type: "confirm",
                                                callback: () => deleteMutate([{id: data?.projectId}])
                                            })
                                        }}
                                    >
                                        <img
                                            src={
                                                process.env.PUBLIC_URL + "/assets/images/icon_cross.png"
                                            }
                                            alt=""
                                        />
                                        삭제
                                    </button>
                                </AuthorityChecker>
                            </>
                        )}
                    </div>
                </div>
                <h5 className="subheading mt-0">프로젝트 기본정보</h5>
                <div className="table_scroll mb-0">
                    <table className="table table-bordered my_table table_detail">
                        <tbody>
                        <tr>
                            <th>프로젝트 명</th>
                            <td colSpan={3}>
                                {watchesBasicInfo?.projectName}
                            </td>
                        </tr>
                        <tr>
                            <th style={{width: "16.66%"}}>생성일자</th>
                            <td style={{width: "33.34%"}}>
                                {mode === "C" ? moment().format("YYYY-MM-DD") : watchesBasicInfo?.regAt}
                            </td>
                            <th style={{width: "16.66%"}}>생성자</th>
                            <td>{mode !== "R" ? user?.userName : watchesBasicInfo?.regName}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content_wrap">
                <h5 className="subheading mt-0">프로젝트 정보</h5>
                <div className="table_scroll mb-0">
                    <table className="table table-bordered my_table table_detail">
                        <tbody>
                        <tr>
                            <th style={{width: "16.66%"}}>프로젝트명</th>
                            <td className={mode !== "R" ? "p-4px" : ""} style={{width: "33.34%"}}>
                                {mode === "R" ? (
                                    watchesBasicInfo?.projectName
                                ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={watchesBasicInfo?.projectName}
                                        {...register("projectName")}
                                    />
                                )}
                            </td>
                            <th style={{width: "16.66%"}}>PM</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? (
                                    watchesBasicInfo?.pmName
                                ) : (
                                    <div className="d-flex">
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("pmName")}
                                            disabled
                                        />
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchUserInner
                                                            confirmCallback={(item) => {
                                                                setValue("pm", item.userId)
                                                                setValue("pmName", item.userName)
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "PM 조회",
                                                    modalSize: "xl",
                                                })
                                            }
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th>고객사</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? watchesBasicInfo?.customerAccountName
                                    : <div className="d-flex">
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("customerAccountName")}
                                            disabled
                                        />
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchBusinessPartnerInner
                                                            confirmCallback={(item) => {
                                                                if (setValue) {
                                                                    setValue("customerAccountId", item?.accountId);
                                                                    setValue("customerAccountName", item?.name)
                                                                }
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "고객사 조회",
                                                    modalSize: "xxl",
                                                })
                                            }
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                    </div>}
                            </td>
                            <th>요청자</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? watchesBasicInfo?.contactName
                                    : <div className="d-flex">
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("contactName")}
                                            disabled
                                        />
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchContactsInner
                                                            confirmCallback={(item) => {
                                                                setValue("contact", item.contactId)
                                                                setValue("contactName", item.fullName)
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "요청자 조회",
                                                    modalSize: "xxl",
                                                })
                                            }
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                    </div>}
                            </td>
                        </tr>
                        <tr>
                            <th>매출처</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? watchesBasicInfo?.salesAccountName
                                    : <div className="d-flex">
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("salesAccountName")}
                                            disabled
                                        />
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchErpInner
                                                            confirmCallback={(item) => {
                                                                if (setValue) {
                                                                    setValue("salesAccountId", item?.new_erp_accountid);
                                                                    setValue("salesAccountName", item?.new_txt_nm_partner)
                                                                }
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "고객사 조회",
                                                    modalSize: "xl",
                                                })
                                            }
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                    </div>}
                            </td>
                            <th></th>
                            <td></td>
                        </tr>
                        <tr>
                            <th>투입 인원</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                <div className="d-flex align-items-center">
                                    {mode === "R" ? (
                                        watchesBasicInfo?.assignedPersonnel && watchesBasicInfo?.assignedPersonnel !== ""
                                            ? <>{watchesBasicInfo?.assignedPersonnel}<span className="mx-1"> 명</span></>
                                            : ""
                                    ) : (
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={watchesBasicInfo?.assignedPersonnel}
                                            {...register("assignedPersonnel")}
                                        />
                                    )}
                                </div>
                            </td>
                            <th>가능성</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                <div className="d-flex align-items-center">
                                    {mode === "R" ? (
                                        watchesBasicInfo?.possibility && watchesBasicInfo?.possibility !== ""
                                            ? <>{watchesBasicInfo?.possibility}<span className="mx-1"> %</span></>
                                            : ""
                                    ) : (
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={watchesBasicInfo?.possibility}
                                            {...register("possibility")}
                                        />
                                    )}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>시작일 {mode !== "R" && <span className="required">*</span>}</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? (
                                    watchesBasicInfo?.startDt
                                ) : (
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={watchesBasicInfo?.startDt}
                                        {...register("startDt")}
                                        max="9999-12-31"
                                    />
                                )}
                            </td>
                            <th>종료일 {mode !== "R" && <span className="required">*</span>}</th>
                            <td className={mode !== "R" ? "p-4px" : ""}>
                                {mode === "R" ? (
                                    watchesBasicInfo?.endDt
                                ) : (
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={watchesBasicInfo?.endDt}
                                        {...register("endDt")}
                                        max="9999-12-31"
                                    />
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th>프로젝트 설명</th>
                            <td className={mode !== "R" ? "p-4px" : ""} colSpan={3}>
                                {mode === "R" ? (
                                    watchesBasicInfo?.description
                                ) : (
                                    <textarea
                                        className="form-control"
                                        value={watchesBasicInfo?.description}
                                        {...register("description")}
                                        rows={5}
                                    />
                                )}
                            </td>
                        </tr>
                        {/*<tr>*/}
                        {/*    <th>비고</th>*/}
                        {/*    <td className={mode !== "R" ? "p-4px" : ""} colSpan={3}>*/}
                        {/*        {mode === "R" ? (*/}
                        {/*            data?.remark*/}
                        {/*        ) : (*/}
                        {/*            <input*/}
                        {/*                type="text"*/}
                        {/*                className="form-control"*/}
                        {/*                {...register("remark")}*/}
                        {/*            />*/}
                        {/*        )}*/}
                        {/*    </td>*/}
                        {/*</tr>*/}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};
